import {
  QueryClient,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { Persister } from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';
import { useEffect, useSyncExternalStore } from 'react';
import { ApiRequestError, api, type SessionInfo } from '@/client/api';
import type { AssignmentPatch } from '@/shared/api';
import { applyAssignmentPatch, blankAssignmentRow } from '@/shared/patch';
import { assignmentKey, type Workspace } from '@/shared/types';

/**
 * The client-side store.
 *
 * The whole workspace is a few tens of kilobytes, so it is fetched once and
 * kept in memory. Screens render from that copy, which is why navigating
 * between them costs no network at all. IndexedDB persistence means a returning
 * visitor sees real data before the first request even leaves the device.
 */

export const WORKSPACE_KEY = ['workspace'] as const;
export const SESSION_KEY = ['session'] as const;

const IDB_KEY = 'vrs-query-cache';

/** Minimal IndexedDB persister — smaller and clearer than pulling in another package. */
export const indexedDbPersister: Persister = {
  persistClient: async (client) => {
    await set(IDB_KEY, client);
  },
  restoreClient: async () => {
    return await get(IDB_KEY);
  },
  removeClient: async () => {
    await del(IDB_KEY);
  },
};

export function createQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        // The Sheet only changes when this app writes to it, so aggressive
        // caching is correct rather than merely convenient.
        staleTime: 60_000,
        gcTime: 1000 * 60 * 60 * 24 * 14,
        retry: (failureCount, error) => {
          if (error instanceof ApiRequestError && error.status < 500) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      mutations: {
        // Queue writes while offline instead of failing them; they replay on
        // reconnect, and survive a reload because the cache is persisted.
        networkMode: 'offlineFirst',
        // A 4xx means the server rejected this exact payload, so replaying it
        // can only fail the same way — it just multiplies one visible failure
        // into three identical ones in the console.
        retry: (failureCount, error) => {
          if (error instanceof ApiRequestError && error.status < 500) return false;
          return failureCount < 2;
        },
      },
    },
  });

  // Mutation defaults must be registered by key for paused mutations to be
  // resumable after the page is closed and reopened.
  client.setMutationDefaults(['assignments'], {
    mutationFn: (patches: unknown) => api.patchAssignments(patches as AssignmentPatch[]),
  });
  client.setMutationDefaults(['volunteer'], {
    mutationFn: (body: unknown) => api.saveVolunteer(body),
  });
  client.setMutationDefaults(['rider'], {
    mutationFn: (body: unknown) => api.saveRider(body),
  });
  client.setMutationDefaults(['settings'], {
    mutationFn: (body: unknown) => api.saveSettings(body),
  });

  return client;
}

// --- reads -----------------------------------------------------------------

export function useSession() {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: api.me,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useWorkspace() {
  return useQuery({
    queryKey: WORKSPACE_KEY,
    queryFn: api.workspace,
  });
}

// --- writes ----------------------------------------------------------------

interface MutationContext {
  previous?: Workspace;
}

function useWorkspaceMutation<TVariables>(
  mutationKey: readonly unknown[],
  optimistic?: (workspace: Workspace, variables: TVariables) => Workspace,
) {
  const queryClient = useQueryClient();

  return useMutation<Workspace, Error, TVariables, MutationContext>({
    mutationKey: mutationKey as unknown[],
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: WORKSPACE_KEY });
      const previous = queryClient.getQueryData<Workspace>(WORKSPACE_KEY);

      if (previous && optimistic) {
        queryClient.setQueryData<Workspace>(WORKSPACE_KEY, optimistic(previous, variables));
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      // Put the old state back rather than leaving a change on screen that
      // never actually happened.
      if (context?.previous) queryClient.setQueryData(WORKSPACE_KEY, context.previous);
    },
    onSuccess: (workspace) => {
      // The server returns the reloaded workspace, so one round-trip both
      // writes and re-syncs.
      queryClient.setQueryData(WORKSPACE_KEY, workspace);
    },
  });
}

export function usePatchAssignments() {
  return useWorkspaceMutation<AssignmentPatch[]>(['assignments'], (workspace, patches) => {
    const byKey = new Map(workspace.assignments.map((a) => [assignmentKey(a.date, a.riderId), a]));

    for (const patch of patches) {
      const key = assignmentKey(patch.date, patch.riderId);
      const base = byKey.get(key) ?? blankAssignmentRow(patch.date, patch.riderId);
      byKey.set(key, applyAssignmentPatch(base, patch));
    }

    const assignments = [...byKey.values()].sort((a, b) =>
      a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
    );
    return { ...workspace, assignments };
  });
}

export function useSaveVolunteer() {
  return useWorkspaceMutation<Record<string, unknown>>(['volunteer'], (workspace, input) => {
    const id = input.id as string | undefined;
    if (!id) return workspace;

    const volunteers = workspace.volunteers.map((v) =>
      v.id === id ? { ...v, ...(input as object) } : v,
    );
    return { ...workspace, volunteers };
  });
}

export function useSaveRider() {
  return useWorkspaceMutation<Record<string, unknown>>(['rider']);
}

export function useSaveSettings() {
  return useWorkspaceMutation<Record<string, unknown>>(['settings'], (workspace, input) => ({
    ...workspace,
    settings: { ...workspace.settings, ...(input as object) },
  }));
}

// --- connectivity ----------------------------------------------------------

function subscribeToOnline(callback: () => void): () => void {
  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);
  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
  };
}

export function useIsOnline(): boolean {
  return useSyncExternalStore(
    subscribeToOnline,
    () => navigator.onLine,
    // Assume online when server-rendered or pre-hydration.
    () => true,
  );
}

/** How many writes are still waiting to reach Google, for the sync indicator. */
export function usePendingWrites(): number {
  const inFlight = useMutationState({
    filters: {
      predicate: (mutation) => mutation.state.status === 'pending' || mutation.state.isPaused,
    },
    select: (mutation) => mutation.mutationId,
  });
  return inFlight.length;
}

/** Replay queued writes as soon as the connection comes back. */
export function useResumeOnReconnect(): void {
  const queryClient = useQueryClient();
  const online = useIsOnline();

  useEffect(() => {
    if (online) void queryClient.resumePausedMutations();
  }, [online, queryClient]);
}

export type { SessionInfo };
